import { SystemStyleObject } from '@chakra-ui/react';
import { zIndices } from 'app/chakra-theme/foundations/z-index';
import { audioPlayerConfig } from 'app/components/AudioPlayer/utils';

interface StorageComponentSxType {
  closeButton: SystemStyleObject;
  base: SystemStyleObject;
  container: SystemStyleObject;
  extended: {
    overlay: SystemStyleObject;
    base: SystemStyleObject;
    audiotitle: SystemStyleObject;
    songTitle: SystemStyleObject;
  };
  closeChevronContainer: SystemStyleObject;
}

interface Config {
  height: number;
  waveformHeight: number;
  transitionMs: number;
}

const getHeight = (waveFormEnabled: boolean, config: Config) => {
  const height = waveFormEnabled ? config.waveformHeight : config.height;
  return `${height}px`;
};

const transitionTypeMap = (
  isOpen: boolean,
  waveFormEnabled: boolean,
  audioPlayerConfig: Config
) => {
  const height = getHeight(waveFormEnabled, audioPlayerConfig);
  return {
    fixed: {
      position: 'fixed',
      height,
      bottom: isOpen ? 0 : `-${height}`,
      left: 0,
      right: 0,
    },
    persistent: {
      height: isOpen ? height : 0,
      visibility: isOpen ? 'visible' : 'hidden',
      overflow: 'hidden',
    },
  };
};

interface AudioPlayerSxProps {
  isOpen: boolean;
  transitionType: 'persistent' | 'fixed';
  waveFormEnabled: boolean;
  extendedPlayer: boolean;
  isMobile: boolean;
}

export const audioPlayerSx: (
  props: AudioPlayerSxProps
) => StorageComponentSxType = ({
  isOpen,
  transitionType,
  waveFormEnabled,
  extendedPlayer,
  isMobile,
}) => ({
  closeButton: {
    width: '20px',
    height: '20px',
    color: 'M.60',
    transition: 'color ease-in-out 200ms',
    _hover: {
      color: 'M.70',
      cursor: 'pointer',
    },
  },
  base: {
    overflow: 'hidden',
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    px: 6,
    py: 0,
    background: 'M.20',
    zIndex: zIndices.popover,
    transition: `height ease-in-out ${audioPlayerConfig.transitionMs}ms`,
    ...transitionTypeMap(isOpen, waveFormEnabled, audioPlayerConfig)[
      transitionType
    ],
    '.chakra-slider': {
      height: `${audioPlayerConfig.height}px`,
    },
    '.chakra-fade': {
      w: 'full',
      display: 'flex',
    },
  },
  extended: {
    overlay: {
      display: extendedPlayer ? 'block' : 'none',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      bg: 'black',
      opacity: 0.8,
      zIndex: zIndices.modal,
    },
    base: {
      zIndex: zIndices.popover,
      roundedTop: 4,
      bg: 'M.20',
      textAlign: 'center',
      p: 4,
      position: 'relative',
    },
    audiotitle: {
      fontWeight: 'medium',
      fontSize: 'lg',
      color: 'white',
      mb: 0.5,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    songTitle: {
      color: 'textSubdued',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  closeChevronContainer: {
    ...(isMobile
      ? { position: 'absolute' }
      : { ml: 5, justifyContent: 'flex-end' }),
    flex: { base: '1 1 10%', lg: '1 1 25%', xl: '1 1 40%' },
    minW: '34px',
    top: extendedPlayer ? 0 : 5,
    right: 0,
    gap: 2,
  },
  container: {
    display: extendedPlayer ? 'block' : 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    margin: '0 auto',
    py: 4,
    px: 0,
    ...(extendedPlayer || isMobile
      ? {}
      : { height: getHeight(waveFormEnabled, audioPlayerConfig) }),
  },
});
